import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useHistory, useLocation, Prompt } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AxiosError } from 'axios';
import { Location } from 'history';

import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Stack, Box, Typography } from '@mui/material';
import {
	Button,
	NotFoundPage,
	WidgetError,
	error,
	success,
	StatusType,
	rem,
	useIsFeatureEnabled,
	Text,
	useCompanyDateTime,
	decoratedAttributes,
} from '@innovyze/stylovyze';
import { formatDateTimeWithFormatDate } from '@innovyze/lib-am-common/Utils';

import {
	getDeteriorationModel,
	getDeteriorationModelStatus,
	getFailureDefinitionAction,
	getGroupSetAction,
	getIndustryStandardCurvesAction,
	listAllFailureDefinitions,
	clearPaginationState,
	listDeteriorationModelAssetsResultsAction,
	resetDeteriorationModelAssetsResults,
} from '@Actions';
import { DeteriorationModelConfigurationTab } from '@Components/deteriorationModel/configurationTab';
import { DeteriorationModelResultsTab } from '@Components/deteriorationModel/resultsTab';
import { RenameDeteriorationModelDialog } from '@Components/deteriorationModel/RenameDeteriorationModelDialog';
import { ROUTES } from '@Pages/PageRoutes';
import {
	runModelById,
	cancelModelRun,
	updateDeteriorationModelName,
	updateFailureDefinition,
} from '@Services/deteriorationModels.services';
import { getApiErrorMessage, isDuplicateNameError } from '@Utils/apiError';
import {
	selectDeteriorationModel,
	selectDeteriorationModelAssetsResults,
	selectIndustryStandardCurves,
	selectModelPaginationState,
} from '@Selectors';
import { useGlobalization } from '@Translations';
import { DeteriorationModelStatus } from '@Types/deteriorationModels.types';
import { FullPageWrapper } from '@Utils/styles';
import { getDefaultAssetsResultsRequest } from '@Utils/assetsResultsRequest';
import { hasRequestedAssetsResultsForRun } from '@Components/deteriorationModel/utils/assetResultsCache';
import { DeteriorationModelGradeSetup } from '@Components/deteriorationModel/gradeSetupTab/DeteriorationModelGradeSetup';
import { ResultsInProgressEmptyState } from '@Components/deteriorationModel/resultsTab/DeteriorationModelResultsTab';

export const DeteriorationModel = () => {
	const { t } = useGlobalization();
	const { id: modelId } = useParams<{ id: string }>();
	const dispatch = useDispatch();

	const history = useHistory();
	const location = useLocation();

	const [isRunning, setIsRunning] = useState(false);
	const [keepsRunning, setKeepsRunning] = useState(false);
	const [isRenameOpen, setIsRenameOpen] = useState(false);
	const [hasUserChangedTab, setHasUserChangedTab] = useState(false);
	const [hasUnsavedCurveChanges, setHasUnsavedCurveChanges] = useState(false);
	const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);

	// For tab navigation
	const [pendingTabIndex, setPendingTabIndex] = useState<number | null>(null);
	// For page navigation
	const [pendingLocation, setPendingLocation] = useState<Location | null>(null);
	const confirmedNavigationRef = useRef(false);

	const deteriorationModel = selectDeteriorationModel();
	const currentDeteriorationModel = deteriorationModel.deteriorationModel;
	const assetResults = selectDeteriorationModelAssetsResults();
	const isGradeSetupEnabled = !!useIsFeatureEnabled('info360-venus-dm-grade-setup');
	const storedPaginationState = selectModelPaginationState(modelId);
	const industryStandardCurves = selectIndustryStandardCurves();
	const { formatDate } = useCompanyDateTime();

	// Returning from asset details remounts this page with the model and its results
	// still in the store. Start on Results then, rather than rendering Configuration for
	// one frame (and firing its fetches) before the landing-tab effect below switches over.
	const [currentTabIndex, setCurrentTabIndex] = useState(() =>
		currentDeteriorationModel?.id === modelId &&
		currentDeteriorationModel.status !== DeteriorationModelStatus.initializing &&
		currentDeteriorationModel.status !== StatusType.failed &&
		hasRequestedAssetsResultsForRun(assetResults, modelId, currentDeteriorationModel.latestRunResultsId) &&
		(assetResults.rows?.length ?? 0) > 0
			? 1
			: 0,
	);

	const formattedLastRun = currentDeteriorationModel?.runAt
		? formatDateTimeWithFormatDate(currentDeteriorationModel.runAt, formatDate)
		: '-';

	const runAdpAttrs = decoratedAttributes({
		modelid: currentDeteriorationModel?.id ?? '',
		modelname: currentDeteriorationModel?.name ?? '',
		assetgroupsetid: currentDeteriorationModel?.assetGroupSetId ?? '',
		failuredefinitionid: currentDeteriorationModel?.failureDefinitionId ?? '',
	});

	useEffect(() => {
		dispatch(getDeteriorationModel({ id: modelId }));
		dispatch(listAllFailureDefinitions());
		// The industry curve catalogue does not depend on the model, so it is fetched only
		// if the store does not already hold it. industryStandardCurves is deliberately not
		// a dependency: this effect runs per model, and re-running it when the curves load
		// would refetch the model and the failure definition list for no reason.
		if (!industryStandardCurves.curves.length) {
			dispatch(getIndustryStandardCurvesAction());
		}

		return () => {
			dispatch(clearPaginationState({ modelId }));
		};
	}, [modelId, dispatch]);

	useEffect(() => {
		if (currentDeteriorationModel?.failureDefinitionId) {
			dispatch(getFailureDefinitionAction(currentDeteriorationModel.failureDefinitionId));
		}
	}, [currentDeteriorationModel?.failureDefinitionId]);

	useEffect(() => {
		if (currentDeteriorationModel?.assetGroupSetId) {
			dispatch(getGroupSetAction(currentDeteriorationModel.assetGroupSetId));
		}
	}, [currentDeteriorationModel?.assetGroupSetId]);

	useEffect(() => {
		if (currentDeteriorationModel?.status === StatusType.inProgress) {
			setIsRunning(true);
			setKeepsRunning(true);
		}
	}, [currentDeteriorationModel?.status]);

	// Poll every second while status is "initializing"
	useEffect(() => {
		let intervalId: NodeJS.Timeout;

		if (currentDeteriorationModel?.status === DeteriorationModelStatus.initializing) {
			intervalId = setInterval(() => {
				dispatch(getDeteriorationModelStatus({ id: modelId }));
			}, 1000);
		}

		return () => {
			if (intervalId) {
				clearInterval(intervalId);
			}
		};
	}, [currentDeteriorationModel?.status]);

	useEffect(() => {
		if (currentDeteriorationModel?.latestRunResultsId) {
			// Results for this run are already loaded or in flight, e.g. on returning from
			// asset details, or because AssetsResults has just fetched after a sort or
			// filter change. AssetsResults owns the exact query; this fetch only exists to
			// find out whether there are results to land on.
			if (hasRequestedAssetsResultsForRun(assetResults, modelId, currentDeteriorationModel.latestRunResultsId)) {
				return;
			}
			// Use saved state if available, otherwise use defaults
			const savedState = storedPaginationState?.results?.assets;
			const requestParams = savedState
				? {
						...savedState,
						modelId: modelId,
						runId: currentDeteriorationModel?.latestRunResultsId || '',
				  }
				: getDefaultAssetsResultsRequest(modelId, currentDeteriorationModel?.latestRunResultsId || '');

			dispatch(listDeteriorationModelAssetsResultsAction(requestParams));
		} else {
			// Reset asset results when there's no run results ID
			dispatch(resetDeteriorationModelAssetsResults());
			setCurrentTabIndex(0);
			setHasUserChangedTab(false); // Reset when there are no results
		}
		// latestRunResultsId is a dependency because the model now stays mounted while it
		// refreshes: a completed run or a cancel changes it without changing the id.
	}, [currentDeteriorationModel?.id, currentDeteriorationModel?.latestRunResultsId, dispatch, storedPaginationState]);

	// Poll every second until status is "completed" or "failed"
	useEffect(() => {
		let intervalId: NodeJS.Timeout;

		if (keepsRunning && currentDeteriorationModel) {
			intervalId = setInterval(() => {
				dispatch(getDeteriorationModelStatus({ id: modelId }));

				// Stop polling if status is completed or failed
				if (
					currentDeteriorationModel.status === StatusType.complete ||
					currentDeteriorationModel.status === StatusType.failed
				) {
					clearInterval(intervalId);
					setKeepsRunning(false);
					setIsRunning(false);
					if (currentDeteriorationModel.status === StatusType.failed) {
						dispatch(error(t('Deterioration model failed to run')));
					}
					onModelUpdate();
				}
			}, 1000);
		}

		return () => {
			if (intervalId) {
				clearInterval(intervalId);
			}
		};
	}, [keepsRunning, currentDeteriorationModel?.status]);

	// Choose the landing tab based on model status (only on initial load, not after a manual tab change).
	useEffect(() => {
		// While initializing, lock on Configuration and allow the auto-switch to fire again once complete.
		if (currentDeteriorationModel?.status === DeteriorationModelStatus.initializing) {
			setCurrentTabIndex(0);
			setHasUserChangedTab(false);
			return;
		}

		// On failure, stay on Configuration so the WidgetError is visible (even if stale results exist).
		// Respect a manual tab change so the user can still inspect those results after seeing the error.
		if (currentDeteriorationModel?.status === StatusType.failed) {
			if (!hasUserChangedTab) {
				setCurrentTabIndex(0);
			}
			return;
		}

		// Once results exist, land on the Results tab (always index 1) unless the user already picked a tab.
		if (!hasUserChangedTab && assetResults.rows && assetResults.rows.length > 0) {
			setCurrentTabIndex(1);
		}
	}, [assetResults.rows, currentDeteriorationModel?.status, hasUserChangedTab]);

	const onModelUpdate = () =>
		dispatch(
			getDeteriorationModel({
				id: modelId,
			}),
		);

	const handleRename = async (newModelName: string) => {
		if (!currentDeteriorationModel) return;
		try {
			await updateDeteriorationModelName({
				modelId: currentDeteriorationModel.id,
				newModelName: newModelName,
			});

			dispatch(success(t('Deterioration model renamed successfully')));
			setIsRenameOpen(false);
			onModelUpdate();
		} catch (e) {
			console.error('Error renaming deterioration model:', e);
			if (isDuplicateNameError(e)) {
				const duplicateError = new Error(getApiErrorMessage(e, t('Enter a unique name')));
				(duplicateError as Error & { isDuplicateName: boolean }).isDuplicateName = true;
				throw duplicateError;
			}
			dispatch(error(t('Unable to rename deterioration model')));
			throw e;
		}
	};

	const handleUpdateFailureDefinition = async (newFailureDefinitionId: string) => {
		if (!currentDeteriorationModel) return;
		try {
			await updateFailureDefinition({
				modelId: currentDeteriorationModel.id,
				newFailureDefinitionId: newFailureDefinitionId,
			});

			dispatch(success(t('Failure definition updated successfully')));
			onModelUpdate();
		} catch (e) {
			console.error('Error updating failure definition:', e);
			dispatch(error(t('Unable to update failure definition')));
		}
	};

	const handleRun = async () => {
		if (!currentDeteriorationModel) return;

		setIsRunning(true);
		try {
			await runModelById(currentDeteriorationModel.id);
			setKeepsRunning(true);
			dispatch(
				success(
					t(
						'Results calculation started — your model is being calculated in the background. We’ll notify you when results are ready.',
					),
				),
			);
			dispatch(getDeteriorationModelStatus({ id: modelId }));
		} catch (e) {
			console.error('Error running deterioration model:', e);

			const axiosError = e as AxiosError;
			const errorMessage =
				(axiosError.response?.data as { error_details?: { error?: string } })?.error_details?.error ||
				t('Unable to run deterioration model');
			dispatch(error(errorMessage));

			setIsRunning(false);
		}
	};

	const handleCancelRun = async () => {
		if (!currentDeteriorationModel) return;

		try {
			await cancelModelRun(currentDeteriorationModel.id);
			setIsRunning(false);
			setKeepsRunning(false);
			dispatch(success(t('Model run cancelled successfully')));
			onModelUpdate();
		} catch (e) {
			console.error('Error cancelling model run:', e);

			const axiosError = e as AxiosError;
			const errorMessage =
				(axiosError.response?.data as { error_details?: { error?: string } })?.error_details?.error ||
				t('Unable to cancel model run');
			dispatch(error(errorMessage));
		}
	};

	const handleTabChange = useCallback(
		(index: number) => {
			if (hasUnsavedCurveChanges && index !== currentTabIndex) {
				setPendingTabIndex(index);
				setShowUnsavedChangesDialog(true);
			} else {
				setCurrentTabIndex(index);
				setHasUserChangedTab(true);
			}
		},
		[hasUnsavedCurveChanges, currentTabIndex],
	);

	const handleBlockedNavigation = useCallback(
		(nextLocation: Location): boolean => {
			if (!confirmedNavigationRef.current && hasUnsavedCurveChanges) {
				setPendingLocation(nextLocation);
				setShowUnsavedChangesDialog(true);
				return false;
			}
			return true;
		},
		[hasUnsavedCurveChanges],
	);

	const handleConfirmNavigation = useCallback(() => {
		setHasUnsavedCurveChanges(false);
		setShowUnsavedChangesDialog(false);

		if (pendingTabIndex !== null) {
			setCurrentTabIndex(pendingTabIndex);
			setHasUserChangedTab(true);
			setPendingTabIndex(null);
		}

		if (pendingLocation !== null) {
			confirmedNavigationRef.current = true;
			history.push(pendingLocation.pathname + pendingLocation.search);
			setPendingLocation(null);
		}
	}, [pendingTabIndex, pendingLocation, history]);

	const handleCancelNavigation = useCallback(() => {
		setShowUnsavedChangesDialog(false);
		setPendingTabIndex(null);
		setPendingLocation(null);
	}, []);

	useEffect(() => {
		confirmedNavigationRef.current = false;
	}, [location]);

	useEffect(() => {
		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			if (hasUnsavedCurveChanges) {
				e.preventDefault();
				e.returnValue = '';
			}
		};
		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => window.removeEventListener('beforeunload', handleBeforeUnload);
	}, [hasUnsavedCurveChanges]);

	if (deteriorationModel.successful === false) {
		return <NotFoundPage />;
	}

	if (!currentDeteriorationModel) {
		return <FullPageWrapper isLoading={true} />;
	}

	let configurationTabContent: JSX.Element;
	if (currentDeteriorationModel.status === DeteriorationModelStatus.initializing) {
		configurationTabContent = (
			<ResultsInProgressEmptyState
				message={t('Calculation in progress, please check back later.')}
				isInProgress={true}
			/>
		);
	} else if (currentDeteriorationModel.status === StatusType.failed) {
		configurationTabContent = (
			<Box>
				<WidgetError message={t('Deterioration model failed to run')} />
				<DeteriorationModelConfigurationTab
					onUpdateFailureDefinition={handleUpdateFailureDefinition}
					onUnsavedChangesChange={setHasUnsavedCurveChanges}
				/>
			</Box>
		);
	} else {
		configurationTabContent = (
			<DeteriorationModelConfigurationTab
				onUpdateFailureDefinition={handleUpdateFailureDefinition}
				onUnsavedChangesChange={setHasUnsavedCurveChanges}
			/>
		);
	}

	return (
		<>
			<Prompt when={hasUnsavedCurveChanges && !confirmedNavigationRef.current} message={handleBlockedNavigation} />

			<Dialog open={showUnsavedChangesDialog} onClose={handleCancelNavigation} maxWidth="sm" fullWidth>
				<DialogTitle sx={{ pb: 2 }}>{t('Unsaved Changes')}</DialogTitle>
				<DialogContent dividers sx={{ py: 3, mt: 0.5 }}>
					<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
						<InfoOutlinedIcon color="action" fontSize="small" sx={{ mt: 0.25 }} />
						<Typography variant="body2" color="text.secondary">
							{t(
								'You have unsaved configuration changes. Go back to configuration to apply updates or discard your changes.',
							)}
						</Typography>
					</Box>
				</DialogContent>
				<DialogActions sx={{ px: 3, py: 2 }}>
					<Button onClick={handleConfirmNavigation} variant="contained" color="error">
						{t('Discard Changes')}
					</Button>
					<Button onClick={handleCancelNavigation} variant="contained" color="primary">
						{t('Go back to Configuration')}
					</Button>
				</DialogActions>
			</Dialog>

			<RenameDeteriorationModelDialog
				isOpen={isRenameOpen}
				setIsOpen={setIsRenameOpen}
				onRename={handleRename}
				modelName={currentDeteriorationModel.name}
			/>

			<FullPageWrapper
				title={deteriorationModel?.deteriorationModel?.name}
				minorHeader={
					<IconButton size="small" onClick={() => setIsRenameOpen(true)}>
						<EditIcon fontSize="small" />
					</IconButton>
				}
				breadcrumbs={ROUTES.deteriorationModel.breadcrumbs}
				headerUnderline
				secondary={
					<Stack direction="row" spacing={2} alignItems="center">
						<Stack direction="column" alignItems="flex-start">
							<Text style={{ fontSize: rem(12), color: '#666' }}>{`${t('Last Run')}: ${formattedLastRun}`}</Text>
							<Text style={{ fontSize: rem(12), color: '#666' }}>
								{`${t('Status')}: ${currentDeteriorationModel.status
									.toString()
									.charAt(0)
									.toUpperCase()}${currentDeteriorationModel.status.toString().slice(1)}`}
							</Text>
						</Stack>
						{currentTabIndex === 0 && currentDeteriorationModel.status !== StatusType.inProgress && (
							<Button
								variant="contained"
								color="primary"
								disabled={isRunning}
								onClick={() => {
									if (!isRunning) handleRun();
								}}
								{...runAdpAttrs}
								adp-click="true"
								adp-component-id="run-deterioration-model"
								adp-component-name="Run Deterioration Model">
								{isRunning ? t('Running') : t('Run Model')}
							</Button>
						)}
						{currentDeteriorationModel.status === StatusType.inProgress && (
							<Button
								variant="contained"
								sx={{
									color: 'white',
									backgroundColor: '#DA5F5B',
									'&:hover': {
										backgroundColor: '#DA5F5B',
										boxShadow: 4,
									},
								}}
								onClick={handleCancelRun}>
								{t('Cancel Run')}
							</Button>
						)}
					</Stack>
				}
				applyPadding={true}
				secondaryNav={{
					items: [
						{
							title: t('Configuration'),
							content: configurationTabContent,
						},
						...(currentDeteriorationModel.status !== DeteriorationModelStatus.initializing
							? [
									{
										title: t('Results'),
										content: (
											<DeteriorationModelResultsTab
												onNavigateToGradeSetup={
													isGradeSetupEnabled &&
													currentDeteriorationModel.latestTrainResultsId &&
													currentDeteriorationModel.failureGradeConfigId
														? () => handleTabChange(2)
														: undefined
												}
											/>
										),
									},
							  ]
							: []),
						...(currentDeteriorationModel.status !== DeteriorationModelStatus.initializing &&
						isGradeSetupEnabled &&
						currentDeteriorationModel.latestTrainResultsId &&
						currentDeteriorationModel.failureGradeConfigId
							? [
									{
										title: t('Grade Setup'),
										content: <DeteriorationModelGradeSetup />,
									},
							  ]
							: []),
					],
					itemIndex: currentTabIndex,
					itemIndexChanged: handleTabChange,
				}}
			/>
		</>
	);
};
